/**
 * Parse error response from API and return user-friendly message
 * @param {Error} error - The error object from axios
 * @param {string} defaultMessage - Default message if no specific error found
 * @returns {string} - User-friendly error message
 */
export const parseErrorMessage = (error, defaultMessage = 'An error occurred') => {
  // Check if error has response data
  if (error.response?.data) {
    const errorData = error.response.data;
    
    // Handle FastAPI validation errors (array of error objects)
    if (Array.isArray(errorData.detail)) {
      const validationErrors = errorData.detail
        .map(err => {
          // Format validation error messages
          const field = err.loc ? err.loc[err.loc.length - 1] : 'field';
          const message = err.msg || err.message || 'Invalid value';
          return `${field}: ${message}`;
        })
        .join(', ');
      return `Validation error: ${validationErrors}`;
    } 
    
    // Handle string error messages from FastAPI
    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }
    
    // Handle object with message property
    if (errorData.message) {
      return errorData.message;
    }
    
    // Handle error property
    if (errorData.error) {
      return errorData.error;
    }
  }
  
  // Handle network errors
  if (error.message === 'Network Error') {
    return 'Network error. Please check your connection.';
  }
  
  // Handle timeout errors
  if (error.code === 'ECONNABORTED') {
    return 'Request timeout. Please try again.';
  }
  
  // Return default message
  return defaultMessage;
};

/**
 * Parse error for specific HTTP status codes
 * @param {Error} error - The error object from axios
 * @returns {string} - Status-specific error message
 */
export const getStatusErrorMessage = (error) => {
  const status = error.response?.status;
  
  switch (status) {
    case 400:
      return parseErrorMessage(error, 'Bad request. Please check your input.');
    case 401:
      return 'Unauthorized. Please login again.';
    case 403:
      return parseErrorMessage(error, 'You do not have permission to perform this action.');
    case 404:
      return 'Resource not found.';
    case 422:
      return parseErrorMessage(error, 'Invalid data provided.');
    case 500:
      return 'Server error. Please try again later.';
    default:
      return parseErrorMessage(error);
  }
};
